import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import { AIR, Readout, StatusBadge, AirFontImport, apiErrorMessage, TearLine } from "./airTheme";

// Our own AirBookingStatus enum values (see AirBooking.java) — the local
// /air/my-bookings API returns these directly, no numeric mapping needed.
const BOOKING_STATUSES = ["INITIATED", "PAYMENT_DONE", "TICKETING_IN_PROGRESS", "CONFIRMED", "TICKETING_FAILED", "CANCELLED", "REFUNDED"];

// WeFlyGo's Ticket_Status_Id (from Air_Reprint, used only in the ticket-detail
// modal below): 0=Payment Pending, 1=Payment Failed, 2=Payment Received,
// 3=OnProgress, 4=Confirmed, 5=Failed, 6=Cancelled, 7=Blocked, 8=Rejected.
const ticketStatusKey = (id) => ({
  0: "INITIATED", 1: "TICKETING_FAILED", 2: "PAYMENT_DONE", 3: "TICKETING_IN_PROGRESS",
  4: "CONFIRMED", 5: "TICKETING_FAILED", 6: "CANCELLED", 7: "TICKETING_IN_PROGRESS", 8: "TICKETING_FAILED",
}[id]);

// Nextra's /eticket flightRecord.booking_status is a free-text string (confirmed/failed/
// cancelled/rejected/void/pending/...), same terminal-failure vocabulary
// AirBookingStatusScheduler.NEXTRA_TERMINAL_FAILURE_STATUSES already uses on the backend.
const NEXTRA_TERMINAL_FAILURE = new Set(["failed", "cancelled", "canceled", "rejected", "void"]);
const nextraBookingStatusKey = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "confirmed") return "CONFIRMED";
  if (NEXTRA_TERMINAL_FAILURE.has(s)) return "TICKETING_FAILED";
  return "TICKETING_IN_PROGRESS";
};

const fmtDateTime = (s) => {
  if (!s) return "--";
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const flightLabel = (b) => {
  const flights = b.flightsMeta || [];
  if (flights.length === 0) return null;
  // airline_name only exists on Nextra bookings (confirmed via /eticket, after the flight is
  // actually ticketed) — WeFlyGo's flightsMeta never had it, so airline_code stays the fallback.
  return flights.map((f) => [f.airline_name || f.airline_code, f.flight_numbers].filter(Boolean).join(" ")).filter(Boolean).join(" · ");
};

const passengerLabel = (b) => {
  const p = (b.passengersMeta || [])[0];
  if (!p) return "--";
  const name = [p.title, p.first_name, p.last_name].filter(Boolean).join(" ");
  return name || "--";
};

const FlightHistory = () => {
  const mid = useSelector((s) => s.auth.employeeDetails?.username);
  const now = new Date();

  const [bookingRefNo, setBookingRefNo] = useState("");
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(now);
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(now.toISOString().split("T")[0]);

  const [bookings, setBookings] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reprintData, setReprintData] = useState(null);
  const [error, setError] = useState("");

  const { callApi: historyApi, loading: fetching } = useApi("/air/my-bookings", "POST");
  const { callApi: reprintApi, loading: reprintingWeFlyGo } = useApi("/air/reprint", "POST");
  const { callApi: nextraEticketApi, loading: reprintingNextra } = useApi("/air/nextra/eticket", "POST");
  const reprinting = reprintingWeFlyGo || reprintingNextra;

  const fetchHistory = async () => {
    setError("");
    setReprintData(null);
    setSelectedBooking(null);
    try {
      const res = await historyApi({
        mid,
        bookingRefNo: bookingRefNo || null,
        status: status || null,
        fromDate: fromDate || null,
        toDate: toDate || null,
        page: 0,
        size: 50,
      });
      const histError = apiErrorMessage(res);
      if (histError) {
        setError(histError);
        setBookings([]);
        setTotalCount(0);
        return;
      }
      setBookings(res?.bookings || []);
      setTotalCount(res?.totalCount || 0);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load history");
    }
  };

  useEffect(() => {
    if (mid) fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mid]);

  // Instant — no API call. /air/my-bookings already returns everything from our
  // own DB: PNR, route/times, passengers, ticket numbers, add-ons, amount, status.
  const handleViewTicket = (booking) => {
    setSelectedBooking(booking);
    setReprintData(null);
    setError("");
  };

  // Optional, on-demand only — never called automatically. Only useful for a
  // fresher live read from the airline; the DB view above already has
  // everything we persist (including ticket numbers, saved centrally by
  // AirReprintService itself on every successful call this triggers).
  const checkLiveStatus = async () => {
    if (!selectedBooking) return;
    setError("");
    try {
      if (selectedBooking.subProvider === "NEXTRA") {
        const res = await nextraEticketApi({ txId: selectedBooking.externalRefId, wsMode: "test" });
        // Nextra's clean failures land as a normal ({error_code, message}) response, not a thrown
        // error — same shape apiErrorMessage already understands for WeFlyGo's Response_Header case.
        const nextraError = apiErrorMessage(res);
        if (nextraError) { setError(nextraError); return; }
        if (!res?.ApiStatus?.Result?.flightRecord) { setError("Could not fetch live ticket details."); return; }
        setReprintData(res);
        return;
      }
      const res = await reprintApi({ bookingRefNo: selectedBooking.externalRefId, airlinePNR: selectedBooking.airlinePnr || "" });
      const reprintError = apiErrorMessage(res);
      if (reprintError) { setError(reprintError); return; }
      if (!res?.AirPNRDetails?.length && !res?.Response_Header) { setError("Could not fetch live ticket details."); return; }
      setReprintData(res);
    } catch (e) {
      setError("Could not load live status. " + (e?.response?.data?.message || e.message || ""));
    }
  };

  // ─── Ticket details — built from our own DB row (/air/my-bookings) ──────
  if (selectedBooking) {
    const pax = selectedBooking.passengersMeta || [];
    const flights = selectedBooking.flightsMeta || [];
    const ticketNumbers = selectedBooking.ticketNumbers || [];
    const addons = selectedBooking.addons || [];
    const addonsTotal = addons.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
    const ticketNoByPax = new Map(ticketNumbers.map((t) => [t.pax_id, t.ticket_number]));

    const isNextra = selectedBooking.subProvider === "NEXTRA";
    const pnrDetail = !isNextra && reprintData ? (reprintData.AirPNRDetails || [])[0] : null;
    const nextraFlightRecord = isNextra && reprintData ? reprintData?.ApiStatus?.Result?.flightRecord : null;

    return (
      <div className="print-area" style={{ maxWidth: 720, margin: "0 auto", padding: "16px 12px" }}>
        <AirFontImport />
        <style>{`
          @media print {
            .air-ticket-no-print { display: none !important; }
            body { background: #fff !important; }
            .bp-navy-fill { background: #fff !important; color: ${AIR.navy} !important; border: 1.5px solid ${AIR.navy} !important; }
            .bp-navy-fill * { color: ${AIR.navy} !important; }
            .bp-card { box-shadow: none !important; border: 1px solid #D8DEE9 !important; }
          }
          @media (max-width: 480px) {
            .print-area { padding-left: 4px !important; padding-right: 4px !important; }
            /* The header band was clipping "Amount Paid" off the right edge — the booking ref
               and the amount block don't both fit on one row once the ref is long. Stack them. */
            .bp-navy-fill { flex-direction: column; align-items: flex-start !important; gap: 14px; }
            .bp-navy-fill > div:last-child { text-align: left !important; align-self: stretch; }
            .air-booking-ref-readout span { font-size: 20px !important; }

            /* Passengers table (Name/Type/Ticket/Status) was overflowing and needing a
               horizontal scroll to see the Status badge. Turn each row into a label:value
               card stack instead — standard responsive-table pattern, desktop untouched. */
            .air-pax-table thead { display: none; }
            .air-pax-table, .air-pax-table tbody, .air-pax-table tr, .air-pax-table td { display: block; width: 100%; }
            .air-pax-table tr { border: 1px solid #F1F5F9 !important; border-radius: 10px; padding: 8px 10px; margin-bottom: 8px; }
            .air-pax-table td {
              display: flex !important; justify-content: space-between; align-items: center;
              padding: 4px 0 !important; white-space: normal !important; border: none !important;
            }
            .air-pax-table td::before {
              content: attr(data-label); font-size: 10px; font-weight: 700; color: #94A3B8;
              text-transform: uppercase; letter-spacing: 0.06em; flex-shrink: 0; margin-right: 10px;
            }
          }
        `}</style>

        <div className="air-ticket-no-print" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
          <button onClick={() => { setSelectedBooking(null); setReprintData(null); setError(""); }} style={{ background: "none", border: "1.5px solid #E5E7EB", cursor: "pointer", padding: "6px 14px", borderRadius: 8, color: "#374151", fontSize: 13, fontWeight: 600 }}>
            ← Back
          </button>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: AIR.navy, flex: 1 }}>Ticket Details</h2>
          <button
            onClick={() => window.print()}
            style={{ display: "flex", alignItems: "center", gap: 6, background: AIR.navy, border: "none", cursor: "pointer", padding: "8px 16px", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 700 }}
          >
            🖨 Print
          </button>
        </div>

        {/* Boarding-pass card — sourced from our own DB */}
        <div className="bp-card" style={{ background: AIR.card, borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 3px rgba(11,26,53,0.08), 0 8px 24px rgba(11,26,53,0.06)" }}>

          {/* Header band — booking ref is the hero readout */}
          <div className="bp-navy-fill" style={{ background: AIR.navy, padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700, marginBottom: 6 }}>Boarding Pass · Booking Ref</div>
              <span className="air-booking-ref-readout"><Readout size={26} weight={700} color="#fff" tracking="0.06em">{selectedBooking.bookingRefId}</Readout></span>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ marginBottom: 8 }}>
                <StatusBadge status={selectedBooking.status} />
              </div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 2 }}>Amount Paid</div>
              <Readout size={17} weight={700} color={AIR.amber}>₹{(selectedBooking.totalAmount || 0).toLocaleString("en-IN")}</Readout>
              {addonsTotal > 0 && (
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 3 }}>
                  Includes ₹{addonsTotal.toLocaleString("en-IN")} add-ons
                </div>
              )}
            </div>
          </div>

          {/* Route strip — the flight legs, from flightsMeta saved at booking time */}
          {flights.length > 0 && (
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #EEF2F7" }}>
              {flights.map((f, i) => (
                <div key={i} style={{ marginBottom: i === flights.length - 1 ? 0 : 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: AIR.ink, marginBottom: 8 }}>
                    {f.airline_name || f.airline_code || "--"}{" "}
                    <Readout size={12} weight={600} color={AIR.label} tracking="0.04em">{f.flight_numbers || ""}</Readout>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto minmax(0,1fr)", alignItems: "center", gap: 12 }}>
                    <div>
                      <Readout size={22} weight={700} color={AIR.navy}>{f.origin || "---"}</Readout>
                      <div style={{ fontSize: 12, color: AIR.label, marginTop: 2 }}>{fmtDateTime(f.departure_dt)}</div>
                    </div>
                    <div style={{ textAlign: "center", padding: "0 6px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, color: AIR.mist }}>
                        <div style={{ width: 24, height: 1, background: AIR.mist }} />
                        <span style={{ fontSize: 13 }}>✈</span>
                        <div style={{ width: 24, height: 1, background: AIR.mist }} />
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <Readout size={22} weight={700} color={AIR.navy}>{f.destination || "---"}</Readout>
                      <div style={{ fontSize: 12, color: AIR.label, marginTop: 2 }}>{fmtDateTime(f.arrival_dt)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <TearLine background={AIR.navy} bg={AIR.paper} />

          {/* Stub — PNR, passengers (+ ticket numbers), add-ons, all from our own DB */}
          <div style={{ padding: "18px 22px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14, marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 11, color: AIR.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>Airline PNR</div>
                <Readout size={15} weight={700} tracking="0.06em">{selectedBooking.airlinePnr || "--"}</Readout>
              </div>
              <div>
                <div style={{ fontSize: 11, color: AIR.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>CRS PNR</div>
                <Readout size={15} weight={700} tracking="0.06em">{selectedBooking.crsPnr || "--"}</Readout>
              </div>
            </div>

            {selectedBooking.passengerMobile && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontWeight: 700, fontSize: 11, color: AIR.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Contact</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, color: AIR.label }}>Mobile</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{selectedBooking.passengerMobile}</div>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: AIR.label }}>Email</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", overflowWrap: "break-word", wordBreak: "break-word" }}>{selectedBooking.passengerEmail || "--"}</div>
                  </div>
                </div>
              </div>
            )}

            {pax.length > 0 && (
              <div>
                <div style={{ fontWeight: 700, fontSize: 11, color: AIR.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Passengers</div>
                <div style={{ overflowX: "auto" }}>
                  <table className="air-pax-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr>
                        {["Name", "Type", "Ticket No.", "Status"].map((h) => (
                          <th key={h} style={{ textAlign: "left", padding: "6px 10px", fontSize: 11, fontWeight: 700, color: AIR.label, borderBottom: `1.5px solid ${AIR.mist}`, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pax.map((p, i) => {
                        const ticketNo = ticketNoByPax.get(p.pax_id);
                        return (
                          <tr key={i} style={{ borderBottom: "1px solid #F3F4F6" }}>
                            <td data-label="Name" style={{ padding: "8px 10px", color: "#111827", fontWeight: 600, whiteSpace: "nowrap" }}>{[p.title, p.first_name, p.last_name].filter(Boolean).join(" ")}</td>
                            <td data-label="Type" style={{ padding: "8px 10px", color: "#6B7280", whiteSpace: "nowrap" }}>{["Adult", "Child", "Infant"][p.pax_type] ?? p.pax_type}</td>
                            <td data-label="Ticket No." style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                              {ticketNo
                                ? <Readout size={13} weight={700} color={AIR.navy}>{ticketNo}</Readout>
                                : <span style={{ fontSize: 12, color: AIR.label, fontStyle: "italic" }}>Pending</span>}
                            </td>
                            <td data-label="Status" style={{ padding: "8px 10px" }}><StatusBadge status={selectedBooking.status} size="sm" /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {addons.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 11, color: AIR.label, textTransform: "uppercase", letterSpacing: "0.06em" }}>Add-ons</div>
                  <Readout size={13} weight={700} color={AIR.amberDeep}>₹{addonsTotal.toLocaleString("en-IN")}</Readout>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr>
                        {["Add-on", "Amount (₹)"].map((h) => (
                          <th key={h} style={{ textAlign: "left", padding: "6px 10px", fontSize: 11, fontWeight: 700, color: AIR.label, borderBottom: `1.5px solid ${AIR.mist}`, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {addons.map((a, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #F3F4F6" }}>
                          <td style={{ padding: "8px 10px", color: "#374151" }}>{a.desc || a.ssr_type_desc || "Add-on"}</td>
                          <td style={{ padding: "8px 10px" }}><Readout size={13} weight={500}>₹{(Number(a.amount) || 0).toLocaleString("en-IN")}</Readout></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Optional — never automatic. Only hits WeFlyGo if the merchant asks. */}
        <div className="air-ticket-no-print" style={{ background: "#F8FAFC", border: "1px solid #EEF2F7", borderRadius: 12, padding: "14px 16px", marginTop: 14 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5, color: "#6B7280" }}>
              The details above are from our records. Want the airline's live status?
            </span>
            <button
              onClick={checkLiveStatus}
              disabled={reprinting}
              style={{ background: reprinting ? "#9CA3AF" : AIR.navy, color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 12.5, fontWeight: 700, cursor: reprinting ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
            >
              {reprinting ? "Checking…" : "Check live status"}
            </button>
          </div>

          {error && (
            <div style={{ marginTop: 12, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 12px", color: "#B91C1C", fontSize: 12.5, lineHeight: 1.5 }}>
              {error} — this is on the airline's side and doesn't affect your booking above.
            </div>
          )}

          {pnrDetail && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #E5E7EB", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: AIR.label, textTransform: "uppercase", letterSpacing: "0.06em" }}>Live status</span>
              <StatusBadge status={ticketStatusKey(pnrDetail.Ticket_Status_Id)} size="sm" />
            </div>
          )}

          {nextraFlightRecord && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #E5E7EB", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: AIR.label, textTransform: "uppercase", letterSpacing: "0.06em" }}>Live status</span>
              <StatusBadge status={nextraBookingStatusKey(nextraFlightRecord.booking_status)} size="sm" />
              {nextraFlightRecord.airline_pnr && (
                <span style={{ fontSize: 12, color: "#6B7280" }}>
                  PNR: <Readout size={12} weight={700} tracking="0.06em">{nextraFlightRecord.airline_pnr}</Readout>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── History list (our own DB — mid-scoped) ────────────────────
  return (
    <div className="air-history-page" style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 16px" }}>
      <AirFontImport />
      <style>{`
        @media (max-width: 640px) {
          .air-history-filter-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .air-history-filter-grid > button { grid-column: 1 / -1; width: 100%; }
          /* MiniLayout (the shared app shell) already pads every page on mobile — this page's
             own padding was stacking on top of that. Can't touch the shell from here. */
          .air-history-page { padding-left: 4px !important; padding-right: 4px !important; }
        }
      `}</style>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 900, color: "#1E3A5F" }}>✈ Flight Booking History</h2>
        <p style={{ margin: 0, fontSize: 13, color: "#9CA3AF" }}>Your own bookings only</p>
      </div>

      {/* Filters */}
      <div style={{ background: "#fff", border: "1.5px solid #E5E7EB", borderRadius: 14, padding: "18px", marginBottom: 18 }}>
        <div className="air-history-filter-grid" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 1fr auto", gap: 12, alignItems: "end" }}>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 4 }}>Booking Ref No.</label>
            <input
              value={bookingRefNo} onChange={(e) => setBookingRefNo(e.target.value)}
              placeholder="e.g. TVL..."
              style={{ width: "100%", height: 40, borderRadius: 8, border: "1.5px solid #D1D5DB", padding: "0 10px", fontSize: 13, outline: "none", boxSizing: "border-box" }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 4 }}>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: "100%", height: 40, borderRadius: 8, border: "1.5px solid #D1D5DB", padding: "0 10px", fontSize: 13, color: "#111827", background: "#fff", outline: "none", boxSizing: "border-box" }}>
              <option value="">All</option>
              {BOOKING_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 4 }}>From Date</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ width: "100%", height: 40, borderRadius: 8, border: "1.5px solid #D1D5DB", padding: "0 10px", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 4 }}>To Date</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ width: "100%", height: 40, borderRadius: 8, border: "1.5px solid #D1D5DB", padding: "0 10px", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
          </div>
          <button
            onClick={fetchHistory}
            disabled={fetching}
            style={{ height: 40, borderRadius: 8, border: "none", background: "#1E3A5F", color: "#fff", fontSize: 13, fontWeight: 700, padding: "0 20px", cursor: fetching ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
          >
            {fetching ? "Loading..." : "Search"}
          </button>
        </div>
      </div>

      {error && !selectedBooking && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 16, color: "#DC2626", fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Stats */}
      {bookings.length > 0 && (
        <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12 }}>
          {bookings.length} of {totalCount} booking{totalCount !== 1 ? "s" : ""}
        </div>
      )}

      {/* Table */}
      {bookings.length === 0 && !fetching ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#9CA3AF" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 15 }}>No bookings found for the selected filters</div>
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1.5px solid #E5E7EB", borderRadius: 14, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#F8FAFC" }}>
                  {["Ref No.", "Passenger", "Route", "Travel Date", "Booked On", "Amount (₹)", "PNR", "Status", ""].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "12px 14px", fontSize: 11, fontWeight: 700, color: "#9CA3AF", borderBottom: "1.5px solid #E5E7EB", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bookings.map((b, i) => (
                  <tr key={b.bookingRefId || i} style={{ borderBottom: "1px solid #F3F4F6" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#F8FAFC")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                  >
                    <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}><Readout size={13} weight={700} color={AIR.navy} tracking="0.04em">{b.bookingRefId}</Readout></td>
                    <td style={{ padding: "12px 14px", color: "#111827" }}>
                      <div>{passengerLabel(b)}</div>
                      {b.passengerMobile && <div style={{ fontSize: 11, color: "#9CA3AF" }}>{b.passengerMobile}</div>}
                    </td>
                    <td style={{ padding: "12px 14px", color: "#374151", whiteSpace: "nowrap" }}>
                      <div>{b.origin && b.destination ? `${b.origin} → ${b.destination}` : "--"}</div>
                      {flightLabel(b) && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{flightLabel(b)}</div>}
                    </td>
                    <td style={{ padding: "12px 14px", color: "#374151", whiteSpace: "nowrap" }}>
                      {b.travelDate || "--"}
                    </td>
                    <td style={{ padding: "12px 14px", color: "#6B7280", whiteSpace: "nowrap" }}>
                      {fmtDateTime(b.createdAt)}
                    </td>
                    <td style={{ padding: "12px 14px" }}>
                      <Readout size={13} weight={700} color={AIR.navy}>₹{(b.totalAmount || 0).toLocaleString("en-IN")}</Readout>
                    </td>
                    <td style={{ padding: "12px 14px" }}>
                      <Readout size={13} weight={700} tracking="0.06em">{b.airlinePnr || "--"}</Readout>
                    </td>
                    <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                      <StatusBadge status={b.status} size="sm" />
                    </td>
                    <td style={{ padding: "12px 14px" }}>
                      <button
                        onClick={() => handleViewTicket(b)}
                        disabled={reprinting && selectedBooking?.bookingRefId === b.bookingRefId}
                        style={{ fontSize: 12, fontWeight: 600, color: AIR.sky, background: "#EFF6FF", border: "none", borderRadius: 6, padding: "5px 10px", cursor: "pointer", whiteSpace: "nowrap" }}
                      >
                        {reprinting && selectedBooking?.bookingRefId === b.bookingRefId ? "Loading..." : "View Ticket"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};

export default FlightHistory;
